import React, { useCallback } from 'react'
import { useGetSsoStartUrlLazyQuery, GetSsoStartUrlQuery } from 'gqlTypes'
import { ErrorHandler } from 'modules/tracing'
import { SsoSignInSource } from '../SsoSignInSource'
import { mapSsoStartUrl } from './mapSsoStartUrl'

export interface GetSsoStartUrlResponse {
  url?: string
  errors: React.ReactNode[]
}

const mapResponse = ({
  source,
  from,
  data,
}: {
  source: SsoSignInSource
  from?: string
  data?: GetSsoStartUrlQuery
}): GetSsoStartUrlResponse => ({
  url: data?.ssoStartUrl.successful
    ? mapSsoStartUrl(data.ssoStartUrl.url!, { source, from })
    : undefined,
  errors:
    data?.ssoStartUrl.errors
      .filter(e => !!e)
      .map(e => {
        if (e!.code === 'NOT_FOUND') {
          return (
            <>
              This Workspace is not currently configured to use SSO. <br />
              Please check your SSO Shortname or sign in with your email and
              password
            </>
          )
        } else {
          return e!.message
        }
      }) ?? [],
})

export const useGetSsoStartUrl = ({
  source,
  from,
  onNetworkError,
  onCompleted,
}: {
  source: SsoSignInSource
  from?: string
  onNetworkError: (message: string) => void
  onCompleted?: () => void
}) => {
  const [getSsoStartUrl, { data, loading }] = useGetSsoStartUrlLazyQuery({
    fetchPolicy: 'network-only',
    onError: () => {
      ErrorHandler.apollo('GetSsoStartUrl', {})
      onNetworkError('Something went wrong')
    },
    onCompleted: () => {
      onCompleted?.()
    },
  })

  const getUrl = useCallback(
    ({ teamName }: { teamName: string }) => {
      getSsoStartUrl({ variables: { teamName: teamName.trim() } })
    },
    [getSsoStartUrl]
  )

  const response = mapResponse({ source, from, data })

  return { getUrl, response, isLoading: loading }
}
